import {Inject, Injectable, OnDestroy} from '@angular/core';
import {APP_CONFIG} from '../app-config.constants';
import {IAppConfig} from '../app-config.interface';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ObjectResponse, SearchResult} from '../classes/object-response';
import {Office} from '../classes/office';
import {Listing} from '../classes/listing';
import {Agent} from '../classes/agent';
import {Pagination} from '../classes/pagination';
import {Helper} from '../classes/helper';
import {SearchFiltersParams} from '../classes/search-filters-params';
import {HasSubscriptions} from '../components/has-subscriptions';
import { map, tap } from 'rxjs/operators';
import { ApiConfigService } from './api-config/api-config.service';
import {PreferencesService} from './preferences.service';
import { CacheService } from './cache.service';
import { CommunicationService } from './communication.service';

@Injectable()
export class OfficeService extends HasSubscriptions implements OnDestroy {

  private _randomSeed: string;
  // browser navigation should use the previously generated seed for the office data 
  // when navigating after component destroy through browser back/forward buttons
  private KeyOfficeRandomSeed = 'office-search-page.randomSeed';
  private _favouriteListings: string[] = [];

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
              private http: HttpClient,
              private apiConfig: ApiConfigService,
              private cache: CacheService,
              private preferencesService: PreferencesService,
              private communicationService: CommunicationService
  ) {
    super();
    this._randomSeed = '' + new Date().getTime();
    this.addSub(this.preferencesService.favouriteListingsChanged.subscribe((favourites) => this._favouriteListings = favourites));
  }

  private getBaseUrl(route: string): string {
    return this.apiConfig.getServerRoot(route);
  }

  /**
   * Retrieves office information
   * @param personifyId
   */
  async getOfficeDetails(id: string, isPersonifyId: boolean): Promise<Office> {
    const base = this.getBaseUrl(this.appConfig.urls.office.base);
    const route = this.appConfig.urls.office.base;
    let params;
    if (isPersonifyId)
      params = new HttpParams()
        .set('PersonifyId', id);
    else
      params = new HttpParams()
        .set('prefixedid', id);

    const hashKey = this.cache.getRequestHash(route, params);
    const hashValue = this.cache.get<Office>(hashKey);

    if (hashValue) {
      return new Promise<Office>((resolve) => resolve(new Office(hashValue)));
    }

    return await this.http.get<ObjectResponse<Office>>(base + route, { params: params, withCredentials: true })
      .pipe(
        tap(res => this.cache.set(hashKey, res.result, 300, false)),
        map(res => new Office(res.result))
      )
      .toPromise();
  }

  /**
   * Get listings that belong to an office
   * @param personifyId
   * @param pagination
   */
  async getListingsPreviewsForOffice(personifyId: number, pagination: Pagination) {
    const base = this.getBaseUrl(this.appConfig.urls.office.listings);
    const params = this._setSearchParams(pagination);

    const result = await this.http.get<ObjectResponse<SearchResult<Listing[]>>>(base + this.appConfig.urls.office.listings + personifyId, {params: params, withCredentials: true })
      .pipe(map(res => {
        res.result.results = res.result.results ? res.result.results.map((r) => new Listing(r, this._favouriteListings)) : [];
        return res.result;
      }))
      .toPromise();
    return result;
  }

  /**
   * Get set of agents for an office
   * @param personifyId
   * @param pagination
   */
  async getAgentsForOffice(personifyId: number, pagination: Pagination) {
    const base = this.getBaseUrl(this.appConfig.urls.office.agents);
    const params = this._setSearchParams(pagination);
    const result = await this.http.get<ObjectResponse<SearchResult<Agent[]>>>(base + this.appConfig.urls.office.agents + personifyId, {params: params, withCredentials: true })
      .pipe(map(res => {
        res.result.results = res.result.results.map((r) => new Agent(r));
        return res.result;
      }))
      .toPromise();
    return result;
  }

  /**
   * Searches across all Offices
   * @param pagination
   * @param searchParams
   */
  async getOffices(pagination: Pagination, searchParams: SearchFiltersParams, category, shouldUseCachedSeed: boolean = false) {
    const base = this.getBaseUrl(this.appConfig.urls.office.agents);
    
    if (!searchParams.randomSeed) {
      const seed = shouldUseCachedSeed ? this.cache.get<string>(this.KeyOfficeRandomSeed, true) : this._randomSeed;
      searchParams.randomSeed = seed;
    }

    this.cache.set(this.KeyOfficeRandomSeed, searchParams.randomSeed, 300, true);

    const params = Helper.setSearchParams(pagination, searchParams, category);

    const result = await this.http.get<ObjectResponse<SearchResult<Office[]>>>(base + this.appConfig.urls.office.search, { params: params, withCredentials: true})
      .pipe(map(res => {
        res.result.results = res.result.results ? res.result.results.map((o) => new Office(o)) : [];
        return res.result;
      }))
      .toPromise();
    return result;
  }

  async getRelatedOffice(lat: number, lng: number, pagination: Pagination): Promise<SearchResult<Office[]>> {
    const base = this.getBaseUrl(this.appConfig.urls.office.searchByDistance);
    let params = new HttpParams();
    params = params.set('location.latitude', '' + lat);
    params = params.set('location.longitude', '' + lng);
    if (pagination) {
      params = params.set('size', '' + pagination.limit);
    }

    return await this.http.get<ObjectResponse<SearchResult<Office[]>>>(base + this.appConfig.urls.office.searchByDistance, {
      params: params,
      withCredentials: true
    }).pipe(map(res => {
      res.result.results = res.result.results ? res.result.results.map((o) => new Office(o)) : [];
      this.communicationService.map.updated.next(res.result);
      return res.result;
    })).toPromise();
  }

  private _setSearchParams(pagination: Pagination): HttpParams {
    let params = new HttpParams()
    params = params.append('from', pagination.offset.toString());
    params = params.append('size', pagination.limit.toString());
    return params;
  }

}
